import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { NotificationService } from '@shared/services/notification.service';
import { PageHeaderComponent } from '@shared/components/layout';
import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';
import { AssignmentManagementService, AssignmentDto, CreateAssignmentDto, UpdateAssignmentDto } from '../../services/assignment-management.service';
import { AreaDto, AreaManagementService } from '../../services/area-management.service';

export interface AssignmentEditorDialogData {
  assignment?: AssignmentDto;
}

@Component({
  selector: 'app-assignment-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDialogModule,
    PageHeaderComponent,
    ModalShellComponent,
  ],
  templateUrl: './assignment-editor.component.html',
  styleUrl: './assignment-editor.component.scss'
})
export class AssignmentEditorComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  assignmentId: number | null = null;
  currentAssignment: AssignmentDto | null = null;
  areaOptions: AreaDto[] = [];

  readonly organizationLevelOptions = [
    { id: 1, name: 'Iglesia Regional' },
    { id: 2, name: 'Area Apostol' },
    { id: 3, name: 'Distrito' },
    { id: 4, name: 'Grupo de Comunidad' },
    { id: 5, name: 'Comunidad' },
  ];

  private destroy$ = new Subject<void>();
  readonly isDialogMode: boolean;

  constructor(
    private fb: FormBuilder,
    private service: AssignmentManagementService,
    private areaService: AreaManagementService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly dialogData: AssignmentEditorDialogData | null,
    @Optional() private readonly dialogRef: MatDialogRef<AssignmentEditorComponent, boolean> | null,
  ) {
    this.initializeForm();
    this.isDialogMode = !!dialogRef;
  }

  ngOnInit(): void {
    this.loadAreas();

    if (this.isDialogMode) {
      const assignment = this.dialogData?.assignment;
      if (assignment) {
        this.assignmentId = assignment.id ?? null;
        this.isEditMode = true;
        this.currentAssignment = assignment;
        this.form.patchValue({
          name: assignment.name,
          areaId: assignment.areaId,
          requiresActiveMinistry: assignment.requiresActiveMinistry ?? false,
          allowedOrganizationLevelIds: assignment.allowedOrganizationLevelIds ?? [],
        });
      }
      return;
    }

    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['id'] && params['id'] !== 'new') {
          this.assignmentId = +params['id'];
          this.isEditMode = true;
          this.loadAssignment();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      areaId: [null as number | null, [Validators.required]],
      requiresActiveMinistry: [false],
      allowedOrganizationLevelIds: [[] as number[], [Validators.required, Validators.minLength(1)]],
    });
  }

  get modalConfig(): ModalShellConfig {
    return {
      title: this.isEditMode ? 'Editar nombramiento' : 'Nuevo nombramiento',
      subtitle: this.isEditMode ? 'Modifica los datos del nombramiento' : 'Define los datos del nuevo nombramiento',
      size: 'md',
      layout: 'form',
    };
  }

  private loadAssignment(): void {
    if (!this.assignmentId) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getById(this.assignmentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (assignment: AssignmentDto) => {
          this.currentAssignment = assignment;
          this.form.patchValue({
            name: assignment.name,
            areaId: assignment.areaId,
            requiresActiveMinistry: assignment.requiresActiveMinistry ?? false,
            allowedOrganizationLevelIds: assignment.allowedOrganizationLevelIds ?? [],
          });
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          console.error('Error loading assignment:', error);
          this.notificationService.error('Error al cargar el nombramiento');
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  saveAssignment(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.notificationService.warning('Por favor, completa todos los campos correctamente');
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    const formValue = this.form.getRawValue() as CreateAssignmentDto;

    if (this.isEditMode && this.assignmentId && this.currentAssignment) {
      const updateDto: UpdateAssignmentDto = {
        ...formValue,
        isActive: this.currentAssignment.isActive ?? true,
        sortOrder: this.currentAssignment.sortOrder ?? 0
      };
      this.updateAssignment(this.assignmentId, updateDto);
    } else {
      this.createAssignment(formValue);
    }
  }

  private createAssignment(dto: CreateAssignmentDto): void {
    this.service.create(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Nombramiento creado exitosamente');
          if (this.dialogRef) {
            this.dialogRef.close(true);
          } else {
            this.router.navigate(['/configuration/assignments']);
          }
        },
        error: (error: any) => {
          console.error('Error creating assignment:', error);
          const message = (error.error as any)?.message || 'Error al crear el nombramiento';
          this.notificationService.error(message);
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  private updateAssignment(id: number, dto: UpdateAssignmentDto): void {
    this.service.update(id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Nombramiento actualizado exitosamente');
          if (this.dialogRef) {
            this.dialogRef.close(true);
          } else {
            this.router.navigate(['/configuration/assignments']);
          }
        },
        error: (error: any) => {
          console.error('Error updating assignment:', error);
          const message = (error.error as any)?.message || 'Error al actualizar el nombramiento';
          this.notificationService.error(message);
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    } else {
      this.router.navigate(['/configuration/assignments']);
    }
  }

  private loadAreas(): void {
    this.areaService.getActive()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: areas => {
          this.areaOptions = areas;
          if (!this.isEditMode && areas.length > 0 && !this.form.get('areaId')?.value) {
            this.form.patchValue({ areaId: areas[0].id });
          }
          this.cdr.markForCheck();
        },
        error: () => this.notificationService.error('No se pudieron cargar las áreas'),
      });
  }
}
